<template>
  <div class="list-item-grid py-1" :class="{ 'protection-issue': isProtectionIssue }">
    <div class="list-item-grid__icon-block">
      <v-icon v-if="deviceIcon" :icon="deviceIcon" size="40" class="mr-4" />
    </div>
    <div class="list-item-grid__details-block d-flex flex-column justify-center pr-2">
      <div :data-testid="`devices-table-name-row-${index}`" class="subtitle2">
        {{ primaryText }}
      </div>
      <div :data-testid="`devices-table-details-row-${index}`" class="body2">
        <span :data-testid="`devices-table-hostname-col-row-${index}`">
          {{ device.hostName }}
        </span>
        <span
          v-if="device.offline"
          class="offline-label"
          :data-testid="`devices-table-is-offline-row-${index}`"
          >{{ ` (${$t("general.offline")})` }}
        </span>
        <slot name="details-extra" :device="device" :index="index" />
      </div>
      <div
        class="body3 white-space-normal text-indigo-medium"
        :class="{ 'reduced-max-width': reducedOsWidth }"
        :data-testid="`devices-table-os-col-row-${index}`"
      >
        {{ device.osDisplayName }}
      </div>
      <template v-if="device.updateStatus">
        <div
          class="d-flex align-center body3"
          :class="getUpdateStatusClass(device.updateStatus)"
          :data-testid="`devices-table-update-status-row-${index}`"
        >
          <span>
            {{ $t(`devices.statuses.${device.updateStatus}`) }}
          </span>
          <v-tooltip
            v-if="device.updateStatus === AgentUpdateStatus.UPDATE_PENDING"
            open-delay="300"
            location="top"
          >
            <template #activator="{ props }">
              <v-icon
                class="ml-1"
                v-bind="props"
                icon="icon-info-solid-xs"
                size="16"
                :data-testid="`devices-table-update-status-icon-row-${index}`"
              />
            </template>
            {{ $t(`devices.tooltips.${device.updateStatus}`) }}
          </v-tooltip>
        </div>
      </template>
      <div
        class="d-flex align-center body3"
        :class="protectionStatusColor"
        :data-testid="`devices-table-protection-status-row-${index}`"
        v-if="device.deviceProtectionStatus"
      >
        <v-icon icon="icon-protection-issue-xs" size="18" class="mr-1" />
        <span>{{ $t(`devices.protectionStatus.${device.deviceProtectionStatus}`) }}</span>
      </div>
    </div>
    <div class="list-item-grid__status-block">
      <v-tooltip v-if="errorsList.length > 0" open-delay="300" location="top">
        <template #activator="{ props }">
          <div class="d-flex align-center text-no-wrap">
            <div class="caption text-red-dark">
              <template v-if="errorsList.length > 1">
                <span class="mr-1">
                  {{ $t("devices.statuses.nIssues", { n: errorsList.length }) }}
                </span>
                <v-icon
                  :data-testid="`devices-table-icon-row-${index}`"
                  icon="$warning"
                  v-bind="props"
                />
              </template>
              <template v-else>
                <span class="mr-1">
                  {{ $t(`devices.statuses.${errorsList[0]}`) }}
                </span>
                <v-icon :data-testid="`devices-table-icon-row-${index}`" icon="$warning" />
              </template>
            </div>
          </div>
        </template>
        <div v-for="error in errorsList" :key="error">
          {{ $t(`devices.statuses.${error}`) }}
        </div>
      </v-tooltip>
      <v-tooltip v-else open-delay="300" location="top">
        <template #activator="{ props }">
          <v-icon
            :data-testid="`devices-table-icon-row-${index}`"
            v-bind="props"
            icon="$circleCheck"
          />
        </template>
        {{ $t("devices.noIssues") }}
      </v-tooltip>
    </div>
    <div v-if="hasLabels" class="list-item-grid__bottom-block">
      <div class="d-flex align-center labels-preview">
        <div v-for="label in filteredLabels" :key="label.id">
          <v-tooltip open-delay="300" location="top">
            <template #activator="{ props }">
              <div
                class="color-preview ml-1"
                :style="{ 'background-color': label.color }"
                v-bind="props"
              />
            </template>
            {{ label.name }}
          </v-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends DeviceListItem">
import { computed } from "vue";
import type { DeviceListItem } from "@/_store/devices.module";
import {
  AgentUpdateStatus,
  DeviceProtectionStatus,
  IsolationStatus,
  OsType,
} from "@/constants/devices";

const props = defineProps<{
  device: T;
  index: number;
}>();

const iconMap: Record<OsType, string> = {
  [OsType.WINDOWS]: "$windowsPc",
  [OsType.OSX]: "$macPc",
  [OsType.IOS]: "$iosDevice",
  [OsType.ANDROID]: "$androidDevice",
  [OsType.MAC_OS]: "$macPc",
  [OsType.LINUX]: "$linuxPc",
};

const deviceIcon = computed(() => (props.device.osType ? iconMap[props.device.osType] : undefined));

const primaryText = computed(() => {
  if (props.device.lastLoggedInUsers?.length) {
    return props.device.lastLoggedInUsers[0];
  }
  return props.device.email ?? "";
});

const filteredLabels = computed(() =>
  props.device.labels.filter((label) => !label.predefined).slice(0, 5)
);
const hasLabels = computed(() => filteredLabels.value.length > 0);

const getUpdateStatusClass = (status?: AgentUpdateStatus): string => {
  switch (status) {
    case AgentUpdateStatus.OUTDATED:
      return "text-red-dark";
    case AgentUpdateStatus.UPDATE_AVAILABLE:
      return "text-yellow-base";
    case AgentUpdateStatus.UPDATE_PENDING:
      return "text-indigo-medium";
    default:
      return "";
  }
};

const isIsolated = computed(() => props.device.isolationStatus === IsolationStatus.ISOLATED);
const reducedOsWidth = computed(() => isIsolated.value || hasLabels.value);

const errorsList = computed(() => {
  const errors: Record<string, boolean> = {
    isolated: isIsolated.value,
    unresolvedTickets: props.device.hasOpenTickets,
    deviceRestartNeeded: props.device.deviceRestartNeeded,
    agentConflict: props.device.agentConflict,
  };
  return Object.keys(errors).filter((key) => errors[key]);
});

const isProtectionIssue = computed(() => {
  return props.device.deviceProtectionStatus === DeviceProtectionStatus.PROTECTION_DISABLED;
});

const protectionStatusColor = computed(() => {
  return props.device.deviceProtectionStatus === DeviceProtectionStatus.PROTECTED
    ? "text-green-dark"
    : "text-red-dark";
});
</script>

<style scoped lang="scss">
.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}

.list-item-grid__bottom-block {
  justify-self: right !important;
}

.text-green-dark,
.text-red-dark {
  :deep(.icon-protection-issue-xs::before) {
    color: currentColor;
  }
}

.list-item-grid.protection-issue {
  .list-item-grid__icon-block,
  .list-item-grid__details-block {
    opacity: 0.65;
  }
}
</style>
